import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { toPng } from "html-to-image";
import JSZip from "jszip";
import { api, formatApiError } from "@/lib/api";
import { Plus, MagnifyingGlass, Trash, IdentificationCard, PencilSimple, X, CurrencyEur, DownloadSimple, UploadSimple, CheckCircle, Warning, ArrowCounterClockwise } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, can } from "@/context/AuthContext";
import PhotoCapture from "@/components/PhotoCapture";
import MemberCard from "@/components/MemberCard";
import { logger } from "@/lib/logger";

const DEFAULT_PRICE_BY_TYPE = {
  oro: 100,
  adulto_general: 80,
  joven: 50,
  infantil: 20,
  nino: 10,
};

const TIPO_LABELS = {
  oro: "Oro",
  adulto_general: "Adulto General",
  joven: "Joven",
  infantil: "Infantil",
  nino: "Niño",
};

const initialForm = {
  numero_socio: "", nombre: "", apellidos: "", dni: "",
  fecha_nacimiento: "", telefono: "", email: "", direccion: "",
  foto_url: null,
  tipo_socio: "adulto_general", temporada: "2026-27", estado: "activo", observaciones: "",
  importe_total: 80, partial_payments: [],
  consentimiento_rgpd: false,
};

const fmtEUR = (n) => (Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const sumPagos = (arr) => (arr || []).reduce((s, p) => s + Number(p.importe || 0), 0);

export default function Socios() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState("");
  const [carneEstado, setCarneEstado] = useState(""); // "" | "pagado" | "pendiente"
  const [tab, setTab] = useState("activos"); // 'activos' | 'bajas'
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null); // { created, skipped, errors[] }
  const [importing, setImporting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // {current, total, zipping?} | null
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkRange, setBulkRange] = useState({ desde: "", hasta: "" });
  const bulkCancelRef = React.useRef(false);
  const fileInputRef = React.useRef(null);
  const navigate = useNavigate();
  const isAdmin = can(user, "admin");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (estado) params.estado = estado;
      const { data } = await api.get("/members", { params });
      setMembers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [search, estado]);

  // Tipos de socio dinámicos (desde /member-types). Fallback a los 5 por defecto.
  const [memberTypes, setMemberTypes] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/member-types");
        if (Array.isArray(data) && data.length > 0) setMemberTypes(data);
      } catch (e) { /* fallback a hardcoded */ }
    })();
  }, []);

  // Mapas derivados (si no hay datos de API, usan los defaults hardcoded)
  const priceByType = useMemo(() => {
    if (memberTypes.length === 0) return DEFAULT_PRICE_BY_TYPE;
    return memberTypes.reduce((acc, t) => { acc[t.key] = Number(t.price) || 0; return acc; }, {});
  }, [memberTypes]);
  const labelByType = useMemo(() => {
    if (memberTypes.length === 0) return TIPO_LABELS;
    return memberTypes.reduce((acc, t) => { acc[t.key] = t.label; return acc; }, {});
  }, [memberTypes]);

  // Debounce 300ms para la búsqueda por texto/estado
  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [load]);

  const onSubmit = async (e) => {
    e.preventDefault();
    // Bloquear creación sin consentimiento RGPD; en edición no se pide de nuevo
    if (!editing && !form.consentimiento_rgpd) {
      toast.error("Debes marcar la aceptación de la Política de Privacidad para crear el socio");
      return;
    }
    try {
      const payload = {
        ...form,
        importe_total: Number(form.importe_total) || 0,
        partial_payments: (form.partial_payments || []).map((p) => ({
          fecha: p.fecha, importe: Number(p.importe) || 0
        })),
      };
      if (editing) {
        await api.put(`/members/${editing.id}`, payload);
        toast.success("Socio actualizado");
      } else {
        await api.post("/members", payload);
        toast.success("Socio creado");
      }
      setShowForm(false);
      setEditing(null);
      setForm(initialForm);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const onEdit = (m) => {
    setEditing(m);
    setForm({
      ...initialForm,
      ...m,
      importe_total: Number(m.importe_total || priceByType[m.tipo_socio] || 0),
      partial_payments: m.partial_payments || [],
    });
    setShowForm(true);
  };

  const onDelete = async (m) => {
    if (!window.confirm(`⚠️ ELIMINACIÓN PERMANENTE\n\nSocio #${m.numero_socio} - ${m.nombre} ${m.apellidos}.\n\nEsta acción NO se puede deshacer.\n\n¿Continuar?`)) return;
    try {
      await api.delete(`/members/${m.id}`);
      toast.success("Socio eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const STATUS_STYLE = {
    activo: "text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/30",
    pendiente: "text-[#F97316] bg-[#F97316]/10 border-[#F97316]/30",
    parcial: "text-[#3B82F6] bg-[#3B82F6]/10 border-[#3B82F6]/30",
    baja: "text-slate-500 bg-zinc-500/10 border-zinc-500/30",
  };

  const visibleMembers = useMemo(() => {
    return members.filter((m) => tab === "bajas" ? m.estado === "baja" : m.estado !== "baja");
  }, [members, tab]);

  const activosCount = members.filter((m) => m.estado !== "baja").length;
  const bajasCount = members.filter((m) => m.estado === "baja").length;

  // Cambio automático de precio al cambiar tipo (sólo si el usuario no lo ha personalizado)
  const setTipo = (tipo) => {
    setForm((f) => {
      const defaultOld = priceByType[f.tipo_socio] ?? 0;
      const shouldUpdate = Number(f.importe_total) === defaultOld;
      return {
        ...f,
        tipo_socio: tipo,
        importe_total: shouldUpdate ? (priceByType[tipo] ?? 0) : f.importe_total,
      };
    });
  };

  const addPartial = () => {
    setForm((f) => ({
      ...f,
      partial_payments: [
        ...(f.partial_payments || []),
        { _key: `pp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, fecha: new Date().toISOString().slice(0, 10), importe: 0 }
      ],
    }));
  };

  const updatePartial = (idx, patch) => {
    setForm((f) => {
      const arr = [...(f.partial_payments || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...f, partial_payments: arr };
    });
  };

  const removePartial = (idx) => {
    setForm((f) => {
      const arr = [...(f.partial_payments || [])];
      arr.splice(idx, 1);
      return { ...f, partial_payments: arr };
    });
  };

  const formPagado = sumPagos(form.partial_payments);
  const formPendiente = Math.max(0, Number(form.importe_total || 0) - formPagado);

  const downloadTemplate = async () => {
    try {
      const { data, headers } = await api.get("/members/import/template.xlsx", { responseType: "blob" });
      const blob = new Blob([data], { type: headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_socios.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Plantilla descargada");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo descargar la plantilla");
    }
  };

  const exportMembersPDF = async (mode = "download") => {
    try {
      const params = {};
      if (search) params.search = search;
      if (estado) params.estado = estado;
      if (tab === "bajas") params.estado = "baja";
      if (carneEstado) params.carne_estado = carneEstado;
      const { data, headers } = await api.get("/members/export.pdf", { params, responseType: "blob" });
      const blob = new Blob([data], { type: headers["content-type"] || "application/pdf" });
      const url = URL.createObjectURL(blob);
      if (mode === "preview") {
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else if (mode === "print") {
        const win = window.open(url, "_blank");
        if (win) {
          win.addEventListener("load", () => { try { win.print(); } catch (_) { /* noop */ } });
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        const a = document.createElement("a");
        a.href = url;
        const match = (headers["content-disposition"] || "").match(/filename="?([^";]+)"?/);
        a.download = match ? match[1] : `listado_socios.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
      toast.success(mode === "preview" ? "Vista previa abierta" : mode === "print" ? "Enviado a imprimir" : "PDF descargado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al exportar PDF");
    }
  };

  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // reset para poder subir el mismo fichero de nuevo
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("El archivo debe ser .xlsx");
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/members/import", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setImportResult(data);
      if (data.created > 0) {
        const parts = [`${data.created} socio(s) importados`];
        if (data.warnings?.length) parts.push(`${data.warnings.length} con avisos`);
        if (data.skipped > 0) parts.push(`${data.skipped} omitido(s)`);
        toast.success(parts.join(" · "));
      } else if (data.skipped > 0) {
        toast.error(`Ningún socio importado — ${data.skipped} filas rechazadas`);
      } else {
        toast.info("El archivo no contenía filas nuevas");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al importar");
    } finally {
      setImporting(false);
    }
  };

  const undoImport = async () => {
    if (!importResult?.batch_id) return;
    if (!window.confirm(`¿Deshacer la última importación?\n\nSe eliminarán ${importResult.created} socio(s) creado(s) en esta operación. Esta acción no se puede rehacer.`)) return;
    try {
      const { data } = await api.delete(`/members/import/batch/${importResult.batch_id}`);
      toast.success(`${data.deleted} socio(s) eliminados. Importación deshecha.`);
      setImportResult(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo deshacer la importación");
    }
  };

  const openBulkModal = () => {
    if (!visibleMembers.length) { toast.error("No hay socios en la vista actual"); return; }
    // Sugerir un rango por defecto de los primeros 50
    const nums = visibleMembers.map((m) => parseInt(m.numero_socio, 10)).filter((n) => !isNaN(n)).sort((a, b) => a - b);
    const first = nums[0];
    const last = nums[Math.min(nums.length - 1, 49)];
    setBulkRange({
      desde: first != null ? String(first).padStart(3, "0") : "",
      hasta: last != null ? String(last).padStart(3, "0") : "",
    });
    setShowBulkModal(true);
  };

  // Cuántos socios quedan en el rango elegido
  const targetsForRange = useMemo(() => {
    const desde = parseInt(bulkRange.desde, 10);
    const hasta = parseInt(bulkRange.hasta, 10);
    if (isNaN(desde) || isNaN(hasta)) return [];
    const lo = Math.min(desde, hasta);
    const hi = Math.max(desde, hasta);
    return visibleMembers.filter((m) => {
      const n = parseInt(m.numero_socio, 10);
      return !isNaN(n) && n >= lo && n <= hi;
    });
  }, [bulkRange, visibleMembers]);

  const withTimeout = (promise, ms, label = "operación") =>
    Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error(`Timeout de ${label} (${ms}ms)`)), ms)),
    ]);

  const downloadAllCards = async (targetsOverride = null) => {
    const targets = targetsOverride ?? visibleMembers;
    if (!targets.length) { toast.error("No hay socios en el rango seleccionado"); return; }
    setShowBulkModal(false);
    bulkCancelRef.current = false;

    // Preparar contenedor off-screen y config del club
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-99999px";
    container.style.top = "0";
    container.style.pointerEvents = "none";
    document.body.appendChild(container);
    const root = createRoot(container);

    setBulkProgress({ current: 0, total: targets.length });
    let cfg = { club_name: "Entradas Deportivas", primary_color: "#007AFF", secondary_color: "#0A2D5C", card_text_color: "#FFFFFF" };
    try {
      const { data } = await api.get("/club-config");
      if (data) cfg = { ...cfg, ...data };
    } catch { /* usa default */ }

    // Precargar fuentes una sola vez para calidad óptima
    try {
      await Promise.all([
        '800 56px "Barlow Condensed"', '800 26px "Barlow Condensed"', '800 22px "Barlow Condensed"',
        '800 20px "Barlow Condensed"', '700 22px "Barlow Condensed"', '700 18px "Barlow Condensed"',
        '700 12px "Barlow Condensed"', '700 10px "Barlow Condensed"', '700 9px "Barlow Condensed"',
        '500 12px "Barlow Condensed"',
      ].map((f) => document.fonts.load(f).catch(() => null)));
      if (document.fonts?.ready) await document.fonts.ready;
    } catch { /* ignore */ }

    const zip = new JSZip();
    let ok = 0, errs = 0;
    const errorRows = [];

    for (let i = 0; i < targets.length; i++) {
      if (bulkCancelRef.current) break;
      const m = targets[i];
      setBulkProgress({ current: i + 1, total: targets.length });
      try {
        // Renderizar el carné y esperar al pintado (con timeout global de 6s)
        await withTimeout(
          (async () => {
            await new Promise((resolve) => {
              root.render(<MemberCard cfg={cfg} socio={m} scale={1} />);
              setTimeout(resolve, 100);
            });
            const cardEl = container.querySelector('[data-testid="member-card"]');
            if (!cardEl) throw new Error("Card element not rendered");
            // Toma la captura con su propio timeout defensivo dentro de toPng
            const dataUrl = await toPng(cardEl, { pixelRatio: 3, cacheBust: true, imagePlaceholder: undefined });
            const base64 = dataUrl.split(",")[1];
            const safeApellidos = (m.apellidos || "").toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "").slice(0, 40);
            const safeNombre = (m.nombre || "").toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "").slice(0, 40);
            zip.file(`carne-${m.numero_socio}-${safeApellidos}-${safeNombre}.png`, base64, { base64: true });
          })(),
          6000,
          `carné ${m.numero_socio}`
        );
        ok++;
      } catch (e) {
        logger.error("Error carné", m.numero_socio, e?.message || e);
        errs++;
        errorRows.push({ numero: m.numero_socio, nombre: `${m.nombre} ${m.apellidos}`, reason: e?.message || "error" });
      }
      // Ceder al event loop para no bloquear UI y liberar memoria
      await new Promise((r) => setTimeout(r, 0));
    }

    root.unmount();
    container.remove();

    if (bulkCancelRef.current) {
      toast.info(`Descarga cancelada · ${ok} generado(s), ${targets.length - ok - errs} pendiente(s)`);
    }

    if (ok === 0) {
      toast.error(`No se pudo generar ningún carné (${errs} error(es))`);
      setBulkProgress(null);
      return;
    }

    try {
      const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } }, (meta) => {
        setBulkProgress({ current: targets.length, total: targets.length, zipping: Math.round(meta.percent) });
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      const nums = targets.map((m) => parseInt(m.numero_socio, 10)).filter((n) => !isNaN(n)).sort((a, b) => a - b);
      const rangeName = nums.length ? `-${String(nums[0]).padStart(3, "0")}-${String(nums[nums.length - 1]).padStart(3, "0")}` : "";
      a.href = url;
      a.download = `carnets-${tab}${rangeName}-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${ok} carné(s) descargado(s)${errs > 0 ? ` · ${errs} con error` : ""}`);
      if (errs > 0 && errorRows.length) {
        logger.warn("Carnés con error:", errorRows);
      }
    } catch (e) {
      toast.error("Error al generar el ZIP");
    } finally {
      setBulkProgress(null);
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#007AFF] font-bold">Módulo 1</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tighter">Socios</h1>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="download-all-cards-btn"
              onClick={openBulkModal}
              disabled={bulkProgress !== null || visibleMembers.length === 0}
              className="border border-slate-300 hover:border-[#007AFF] hover:text-[#007AFF] disabled:opacity-50 text-slate-800 px-4 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm active:scale-[0.98] transition"
              style={{ borderRadius: 8 }}
              title="Descarga en ZIP los carnés en el rango que elijas"
            >
              <IdentificationCard size={16} weight="bold" />
              {bulkProgress
                ? (bulkProgress.zipping !== undefined
                    ? `Comprimiendo ${bulkProgress.zipping}%`
                    : `Generando ${bulkProgress.current}/${bulkProgress.total}`)
                : "Descargar carnés"}
            </button>
            {bulkProgress && (
              <button
                data-testid="bulk-cancel-btn"
                onClick={() => { bulkCancelRef.current = true; toast.info("Cancelando…"); }}
                className="h-11 px-3 flex items-center gap-1 border border-[#EF4444] bg-white hover:bg-[#EF4444]/5 text-[#EF4444] font-bold uppercase tracking-widest text-xs transition"
                style={{ borderRadius: 8 }}
              >
                <X size={14} /> Cancelar
              </button>
            )}
            <button
              data-testid="download-template-btn"
              onClick={downloadTemplate}
              className="border border-slate-300 hover:border-slate-900 hover:bg-slate-50 text-slate-800 px-4 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm active:scale-[0.98] transition"
              style={{ borderRadius: 8 }}
              title="Descarga la plantilla Excel para importar socios"
            >
              <DownloadSimple size={16} weight="bold" /> Plantilla
            </button>
            <div className="relative inline-flex" data-testid="pdf-menu-wrap">
              <button
                data-testid="export-socios-pdf-btn"
                onClick={() => exportMembersPDF("preview")}
                className="border border-[#dc2626]/40 hover:border-[#dc2626] hover:bg-[#dc2626]/5 text-[#dc2626] px-4 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm active:scale-[0.98] transition rounded-l-lg"
                style={{ borderTopLeftRadius: 8, borderBottomLeftRadius: 8 }}
                title="Vista previa del listado de socios en PDF"
              >
                <DownloadSimple size={16} weight="bold" /> Listado PDF
              </button>
              <button
                data-testid="export-socios-pdf-download"
                onClick={() => exportMembersPDF("download")}
                className="border border-l-0 border-[#dc2626]/40 hover:border-[#dc2626] hover:bg-[#dc2626]/5 text-[#dc2626] px-3 h-11 flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-widest active:scale-[0.98] transition"
                title="Descargar el PDF"
              >
                ↓
              </button>
              <button
                data-testid="export-socios-pdf-print"
                onClick={() => exportMembersPDF("print")}
                className="border border-l-0 border-[#dc2626]/40 hover:border-[#dc2626] hover:bg-[#dc2626]/5 text-[#dc2626] px-3 h-11 flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-widest active:scale-[0.98] transition"
                style={{ borderTopRightRadius: 8, borderBottomRightRadius: 8 }}
                title="Imprimir el PDF"
              >
                🖨
              </button>
            </div>
            <button
              data-testid="import-excel-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-4 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm active:scale-[0.98] transition"
              style={{ borderRadius: 8 }}
              title="Importa un fichero .xlsx con socios en bloque"
            >
              <UploadSimple size={16} weight="bold" /> {importing ? "Importando..." : "Importar Excel"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={onImportFile}
              className="hidden"
              data-testid="import-file-input"
            />
            <button
              data-testid="recalc-btn"
              onClick={async () => {
                if (!window.confirm("¿Recalcular todos los números de socio? Los socios en BAJA no se renumeran.")) return;
                try {
                  const { data } = await api.post("/members/recalculate");
                  toast.success(`Recalculado: ${data.changes} cambios sobre ${data.total} socios`);
                  load();
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }}
              className="bg-slate-900 text-white px-4 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm active:scale-[0.98] transition"
              style={{ borderRadius: 8 }}
            >
              Recalcular
            </button>
            <button
              data-testid="add-socio-btn"
              onClick={() => { setShowForm(true); setEditing(null); setForm(initialForm); }}
              className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-5 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm active:scale-[0.98] transition shadow-md shadow-blue-500/20"
              style={{ borderRadius: 8 }}
            >
              <Plus size={18} weight="bold" /> Nuevo Socio
            </button>
          </div>
        )}
      </div>

      {/* Import Result Panel */}
      {importResult && (
        <div
          className="border p-4 flex flex-col gap-3 shadow-card"
          style={{
            borderRadius: 12,
            background: importResult.errors?.length
              ? "rgba(239,68,68,0.05)"
              : importResult.warnings?.length
                ? "rgba(249,115,22,0.05)"
                : "rgba(34,197,94,0.05)",
            borderColor: importResult.errors?.length
              ? "rgba(239,68,68,0.4)"
              : importResult.warnings?.length
                ? "rgba(249,115,22,0.4)"
                : "rgba(34,197,94,0.4)",
          }}
          data-testid="import-result-panel"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              {importResult.errors?.length || importResult.warnings?.length
                ? <Warning size={24} weight="bold" className="text-[#F97316]" />
                : <CheckCircle size={24} weight="bold" className="text-[#22C55E]" />
              }
              <div>
                <div className="font-display font-bold uppercase text-lg tracking-tight">Resultado de la importación</div>
                <div className="text-sm text-slate-600 mt-0.5">
                  <span className="text-[#16a34a] font-bold" data-testid="import-created">{importResult.created}</span> creado(s)
                  {importResult.warnings?.length > 0 && (
                    <> · <span className="text-[#F97316] font-bold" data-testid="import-warnings-count">{importResult.warnings.length}</span> con avisos</>
                  )}
                  {importResult.skipped > 0 && (
                    <> · <span className="text-[#EF4444] font-bold" data-testid="import-skipped">{importResult.skipped}</span> omitido(s)</>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {importResult.batch_id && importResult.created > 0 && (
                <button
                  onClick={undoImport}
                  className="h-9 px-3 flex items-center gap-1.5 border border-[#EF4444]/40 bg-white hover:bg-[#EF4444]/5 text-[#EF4444] text-xs font-bold uppercase tracking-widest transition"
                  style={{ borderRadius: 6 }}
                  data-testid="import-undo-btn"
                  title="Elimina todos los socios creados en esta importación"
                >
                  <ArrowCounterClockwise size={14} weight="bold" /> Deshacer
                </button>
              )}
              <button
                onClick={() => setImportResult(null)}
                className="h-8 w-8 flex items-center justify-center border border-slate-200 hover:border-slate-500 text-slate-500"
                style={{ borderRadius: 6 }}
                data-testid="import-result-close"
                title="Cerrar"
              >
                <X size={14} />
              </button>
            </div>
          </div>
          {importResult.errors?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#EF4444] mb-1.5">Errores ({importResult.errors.length})</div>
              <div className="max-h-40 overflow-y-auto text-xs bg-white/60 border border-[#EF4444]/30 p-3 space-y-1" style={{ borderRadius: 8 }}>
                {importResult.errors.map((err, i) => (
                  <div key={`err-${err.row}-${i}`} className="flex gap-2">
                    <span className="text-slate-500 font-bold w-16">Fila {err.row}:</span>
                    <span className="text-slate-700">{err.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {importResult.warnings?.length > 0 && (
            <div data-testid="import-warnings-panel">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#F97316] mb-1.5">Avisos ({importResult.warnings.length}) — filas importadas con ajustes</div>
              <div className="max-h-40 overflow-y-auto text-xs bg-white/60 border border-[#F97316]/30 p-3 space-y-1" style={{ borderRadius: 8 }}>
                {importResult.warnings.map((w, i) => (
                  <div key={`warn-${w.row}-${i}`} className="flex gap-2">
                    <span className="text-slate-500 font-bold w-16">Fila {w.row}:</span>
                    <span className="text-slate-700">{w.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs Activos / Bajas */}
      <div className="flex gap-2 border-b border-slate-200">
        <TabButton active={tab === "activos"} onClick={() => setTab("activos")} testid="tab-activos">
          Activos <span className="ml-2 text-xs opacity-70">({activosCount})</span>
        </TabButton>
        <TabButton active={tab === "bajas"} onClick={() => setTab("bajas")} testid="tab-bajas">
          Bajas <span className="ml-2 text-xs opacity-70">({bajasCount})</span>
        </TabButton>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="socios-search"
            placeholder="Buscar por nombre, DNI, número..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white border border-slate-200 pl-10 pr-4 h-11 text-slate-900 focus:border-[#2563EB] outline-none placeholder:text-slate-400"
          />
        </div>
        {tab === "activos" && (
          <select
            data-testid="socios-filter-estado"
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
            className="bg-white border border-slate-200 px-4 h-11 text-slate-900 focus:border-[#2563EB] outline-none"
          >
            <option value="">Todos (sin bajas)</option>
            <option value="activo">Activo</option>
            <option value="pendiente">Pendiente</option>
            <option value="parcial">Parcial</option>
          </select>
        )}
        <select
          data-testid="socios-filter-carne"
          value={carneEstado}
          onChange={(e) => setCarneEstado(e.target.value)}
          className="bg-white border border-slate-200 px-4 h-11 text-slate-900 focus:border-[#2563EB] outline-none"
          title="Filtrar por estado del carné (afecta al listado PDF)"
        >
          <option value="">Carné: todos</option>
          <option value="pagado">Carné pagado</option>
          <option value="pendiente">Carné pendiente</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 overflow-x-auto" style={{ borderRadius: 6 }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              <Th>Nº</Th>
              <Th>Nombre</Th>
              <Th className="hidden sm:table-cell">DNI</Th>
              <Th className="hidden md:table-cell">Tipo</Th>
              <Th>Estado</Th>
              {tab === "activos" && <Th className="text-right hidden lg:table-cell">Cuota / Pendiente</Th>}
              <Th className="text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-center py-8 text-slate-500">Cargando...</td></tr>
            )}
            {!loading && visibleMembers.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-slate-500">
                {tab === "bajas" ? "Sin socios dados de baja" : "Sin socios registrados"}
              </td></tr>
            )}
            {visibleMembers.map((m) => {
              const total = Number(m.importe_total || 0);
              const pagado = sumPagos(m.partial_payments);
              const pendiente = Math.max(0, total - pagado);
              return (
                <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50 transition" data-testid={`socio-row-${m.numero_socio}`}>
                  <td className="px-4 py-3 font-display font-bold text-lg">{m.numero_socio}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{m.nombre} {m.apellidos}</div>
                    <div className="text-xs text-slate-500 sm:hidden">{m.dni}</div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-slate-600">{m.dni}</td>
                  <td className="px-4 py-3 hidden md:table-cell capitalize text-slate-600">{labelByType[m.tipo_socio] || m.tipo_socio}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border ${STATUS_STYLE[m.estado]}`} style={{ borderRadius: 4 }}>
                      {m.estado}
                    </span>
                  </td>
                  {tab === "activos" && (
                    <td className="px-4 py-3 text-right hidden lg:table-cell">
                      <div className="text-xs text-slate-500">{fmtEUR(total)}</div>
                      <div className={`text-sm font-bold ${pendiente > 0 ? "text-[#F97316]" : "text-[#22C55E]"}`}>
                        {pendiente > 0 ? `Pdte: ${fmtEUR(pendiente)}` : "OK"}
                      </div>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        data-testid={`socio-carnet-${m.numero_socio}`}
                        onClick={() => navigate(`/socios/${m.id}`)}
                        className="h-8 w-8 flex items-center justify-center border border-slate-200 hover:border-[#007AFF] hover:text-[#007AFF] transition"
                        style={{ borderRadius: 6 }}
                        title="Ver carné"
                      >
                        <IdentificationCard size={16} />
                      </button>
                      {isAdmin && (
                        <>
                          <button
                            data-testid={`socio-edit-${m.numero_socio}`}
                            onClick={() => onEdit(m)}
                            className="h-8 w-8 flex items-center justify-center border border-slate-200 hover:border-[#007AFF] hover:text-[#007AFF] transition"
                            style={{ borderRadius: 6 }}
                          >
                            <PencilSimple size={16} />
                          </button>
                          <button
                            data-testid={`socio-delete-${m.numero_socio}`}
                            onClick={() => onDelete(m)}
                            className="h-8 w-8 flex items-center justify-center border border-slate-200 hover:border-[#EF4444] hover:text-[#EF4444] transition"
                            style={{ borderRadius: 6 }}
                          >
                            <Trash size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal form */}
      {/* Bulk download modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 w-full max-w-lg p-6 space-y-4" style={{ borderRadius: 12 }} data-testid="bulk-download-modal">
            <div>
              <div className="text-[11px] tracking-[0.25em] uppercase text-[#007AFF] font-bold">Descarga en bloque</div>
              <h2 className="font-display font-bold text-2xl uppercase mt-1">Descargar carnés</h2>
              <p className="text-sm text-slate-500 mt-1">
                Elige un rango de nº de socio. Recomendamos lotes de <b>50 socios como máximo</b> para evitar sobrecargar el navegador.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-[10px] tracking-[0.2em] uppercase font-bold text-slate-600 mb-1.5">Desde nº</span>
                <input
                  data-testid="bulk-desde"
                  value={bulkRange.desde}
                  onChange={(e) => setBulkRange({ ...bulkRange, desde: e.target.value.replace(/[^0-9]/g, "") })}
                  className="w-full bg-slate-50 border border-slate-200 px-3 h-10 text-slate-900 focus:border-[#2563EB] outline-none"
                  placeholder="001"
                />
              </label>
              <label className="block">
                <span className="block text-[10px] tracking-[0.2em] uppercase font-bold text-slate-600 mb-1.5">Hasta nº</span>
                <input
                  data-testid="bulk-hasta"
                  value={bulkRange.hasta}
                  onChange={(e) => setBulkRange({ ...bulkRange, hasta: e.target.value.replace(/[^0-9]/g, "") })}
                  className="w-full bg-slate-50 border border-slate-200 px-3 h-10 text-slate-900 focus:border-[#2563EB] outline-none"
                  placeholder="050"
                />
              </label>
            </div>

            {/* Quick presets */}
            <div className="flex flex-wrap gap-2">
              {(() => {
                const nums = visibleMembers.map((m) => parseInt(m.numero_socio, 10)).filter((n) => !isNaN(n)).sort((a, b) => a - b);
                if (!nums.length) return null;
                const min = nums[0], max = nums[nums.length - 1];
                const presets = [
                  { label: "1-50", d: min, h: Math.min(min + 49, max) },
                  { label: "51-100", d: Math.min(min + 50, max), h: Math.min(min + 99, max) },
                  { label: "101-200", d: Math.min(min + 100, max), h: Math.min(min + 199, max) },
                  { label: `Todos (${nums.length})`, d: min, h: max },
                ];
                return presets.map((p, i) => (
                  <button
                    key={p.label}
                    type="button"
                    data-testid={`bulk-preset-${i}`}
                    onClick={() => setBulkRange({ desde: String(p.d).padStart(3, "0"), hasta: String(p.h).padStart(3, "0") })}
                    className="h-8 px-3 text-xs border border-slate-200 hover:border-[#2563EB] hover:text-[#2563EB] font-bold uppercase tracking-widest"
                    style={{ borderRadius: 6 }}
                  >
                    {p.label}
                  </button>
                ));
              })()}
            </div>

            <div
              className="p-3 border flex items-center justify-between"
              style={{
                borderRadius: 8,
                borderColor: targetsForRange.length > 100 ? "rgba(249,115,22,0.4)" : "rgba(37,99,235,0.3)",
                background: targetsForRange.length > 100 ? "rgba(249,115,22,0.05)" : "rgba(37,99,235,0.03)",
              }}
            >
              <div className="text-sm">
                <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">Se descargarán</div>
                <div className="font-display font-bold text-3xl tracking-tighter mt-0.5" data-testid="bulk-count">
                  {targetsForRange.length}
                </div>
                <div className="text-xs text-slate-500">carné(s)</div>
              </div>
              {targetsForRange.length > 100 && (
                <div className="text-xs text-[#F97316] max-w-[60%] text-right">
                  <b>Aviso:</b> con más de 100 socios el proceso puede tardar 2-5 min y consumir bastante memoria. Considera reducir el rango.
                </div>
              )}
              {targetsForRange.length === 0 && (
                <div className="text-xs text-slate-500 max-w-[60%] text-right">
                  Ningún socio en este rango.
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowBulkModal(false)}
                className="px-5 h-11 border border-slate-200"
                style={{ borderRadius: 6 }}
                data-testid="bulk-cancel-modal"
              >
                Cancelar
              </button>
              <button
                type="button"
                data-testid="bulk-start-btn"
                onClick={() => downloadAllCards(targetsForRange)}
                disabled={targetsForRange.length === 0}
                className="bg-[#2563EB] hover:bg-[#1d4ed8] disabled:opacity-50 text-white px-5 h-11 font-display font-bold uppercase tracking-wide flex items-center gap-2"
                style={{ borderRadius: 6 }}
              >
                <DownloadSimple size={16} weight="bold" /> Descargar {targetsForRange.length} carné(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <form
            onSubmit={onSubmit}
            className="bg-white border border-slate-200 w-full max-w-3xl my-8 p-6 space-y-4"
            style={{ borderRadius: 6 }}
          >
            <h2 className="font-display font-bold text-2xl uppercase tracking-tight">
              {editing ? "Editar Socio" : "Nuevo Socio"}
            </h2>

            <PhotoCapture
              value={form.foto_url}
              onChange={(url) => setForm({ ...form, foto_url: url })}
              testid="socio-photo"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Nº Socio (vacío = automático)">
                <input data-testid="form-numero-socio" disabled={!!editing} value={form.numero_socio} onChange={(e) => setForm({ ...form, numero_socio: e.target.value })} className={inputCls} placeholder="Auto" />
              </Field>
              <Field label="DNI">
                <input data-testid="form-dni" value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value.toUpperCase() })} className={inputCls} placeholder="Opcional" />
              </Field>
              <Field label="Nombre" required>
                <input data-testid="form-nombre" required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Apellidos" required>
                <input data-testid="form-apellidos" required value={form.apellidos} onChange={(e) => setForm({ ...form, apellidos: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Fecha nacimiento">
                <input type="date" value={form.fecha_nacimiento || ""} onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Teléfono">
                <input value={form.telefono || ""} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Email">
                <input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Dirección">
                <input value={form.direccion || ""} onChange={(e) => setForm({ ...form, direccion: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Tipo de socio">
                <select data-testid="form-tipo" value={form.tipo_socio} onChange={(e) => setTipo(e.target.value)} className={inputCls}>
                  {(memberTypes.length > 0 ? memberTypes : Object.keys(DEFAULT_PRICE_BY_TYPE).map((k) => ({ key: k, label: TIPO_LABELS[k], price: DEFAULT_PRICE_BY_TYPE[k] }))).map((t) => (
                    <option key={t.key} value={t.key}>{t.label} — {Number(t.price).toLocaleString("es-ES")} €</option>
                  ))}
                </select>
              </Field>
              <Field label="Temporada" required>
                <input required value={form.temporada} onChange={(e) => setForm({ ...form, temporada: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Precio por tipo (€)" required>
                <input
                  data-testid="form-importe-total"
                  type="number" step="0.01" min="0" required
                  value={form.importe_total}
                  onChange={(e) => setForm({ ...form, importe_total: parseFloat(e.target.value) || 0 })}
                  className={inputCls}
                />
              </Field>
              <Field label="Estado">
                <select data-testid="form-estado" value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })} className={inputCls}>
                  <option value="activo">Activo</option>
                  <option value="pendiente">Pendiente de pago</option>
                  <option value="parcial">Parcial (pagos a cuenta)</option>
                  <option value="baja">Baja</option>
                </select>
              </Field>
            </div>

            {/* Partial payments */}
            <div className="border border-slate-200 p-4 space-y-3" style={{ borderRadius: 8 }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CurrencyEur size={18} className="text-[#2563EB]" weight="bold" />
                  <h3 className="font-display font-bold uppercase tracking-tight">Pagos parciales</h3>
                </div>
                <button
                  type="button"
                  data-testid="add-partial-btn"
                  onClick={addPartial}
                  className="text-xs bg-[#2563EB] text-white px-3 h-8 flex items-center gap-1 font-bold uppercase tracking-wider active:scale-[0.98]"
                  style={{ borderRadius: 6 }}
                >
                  <Plus size={14} weight="bold" /> Añadir pago
                </button>
              </div>

              {(form.partial_payments || []).length === 0 && (
                <div className="text-center text-slate-500 text-xs py-3">Sin pagos parciales registrados</div>
              )}
              {(form.partial_payments || []).map((p, idx) => (
                <div key={p._key || `pp-idx-${idx}`} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid={`partial-row-${idx}`}>
                  <Field label="Fecha">
                    <input type="date" value={p.fecha || ""} onChange={(e) => updatePartial(idx, { fecha: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label="Importe €">
                    <input
                      type="number" step="0.01" min="0"
                      data-testid={`partial-importe-${idx}`}
                      value={p.importe}
                      onChange={(e) => updatePartial(idx, { importe: parseFloat(e.target.value) || 0 })}
                      className={inputCls}
                    />
                  </Field>
                  <button
                    type="button"
                    data-testid={`partial-remove-${idx}`}
                    onClick={() => removePartial(idx)}
                    className="h-10 w-10 flex items-center justify-center border border-slate-200 hover:border-[#EF4444] hover:text-[#EF4444] transition"
                    style={{ borderRadius: 6 }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}

              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-100 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Cuota total</div>
                  <div className="font-display font-bold text-lg">{fmtEUR(form.importe_total)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Pagado</div>
                  <div className="font-display font-bold text-lg text-[#16a34a]" data-testid="form-pagado">{fmtEUR(formPagado)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Pendiente</div>
                  <div className="font-display font-bold text-lg text-[#F97316]" data-testid="form-pendiente">{fmtEUR(formPendiente)}</div>
                </div>
              </div>
            </div>

            <Field label="Observaciones">
              <textarea rows={2} value={form.observaciones || ""} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} className={inputCls} />
            </Field>

            {!editing && (
              <label
                data-testid="consent-rgpd-wrapper"
                className={`flex items-start gap-3 p-4 border cursor-pointer transition ${form.consentimiento_rgpd ? "border-emerald-500 bg-emerald-50/60" : "border-slate-300 bg-slate-50"}`}
                style={{ borderRadius: 8 }}
              >
                <input
                  data-testid="consent-rgpd-checkbox"
                  type="checkbox"
                  checked={!!form.consentimiento_rgpd}
                  onChange={(e) => setForm({ ...form, consentimiento_rgpd: e.target.checked })}
                  className="mt-1 h-4 w-4 accent-emerald-600 shrink-0"
                />
                <div className="text-xs text-slate-700 leading-relaxed">
                  <span className="font-bold uppercase tracking-widest text-[11px] block mb-1 text-slate-900">Consentimiento RGPD</span>
                  El socio ha sido informado y acepta el tratamiento de sus datos personales según nuestra{" "}
                  <a href="/privacidad" target="_blank" rel="noreferrer" className="text-[#2563EB] underline hover:text-[#1d4ed8]">Política de Privacidad</a>{" "}
                  y el <a href="/aviso-legal" target="_blank" rel="noreferrer" className="text-[#2563EB] underline hover:text-[#1d4ed8]">Aviso Legal</a>. Este consentimiento queda registrado con fecha y hora.
                </div>
              </label>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-5 h-11 border border-slate-200 hover:border-zinc-500 transition" style={{ borderRadius: 6 }}>
                Cancelar
              </button>
              <button data-testid="form-save-socio" type="submit" className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-5 h-11 font-display font-bold uppercase tracking-wide" style={{ borderRadius: 6 }}>
                {editing ? "Guardar" : "Crear Socio"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-slate-50 border border-slate-200 px-3 h-10 text-slate-900 focus:border-[#2563EB] outline-none placeholder:text-slate-400";

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-bold ${className}`}>{children}</th>;
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`px-5 h-10 font-display font-bold uppercase tracking-wider text-sm transition ${
        active
          ? "text-[#2563EB] border-b-2 border-[#2563EB] -mb-px"
          : "text-slate-500 hover:text-slate-800 border-b-2 border-transparent"
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.2em] uppercase font-bold text-slate-600 mb-1.5">
        {label}{required && <span className="text-[#EF4444]"> *</span>}
      </span>
      {children}
    </label>
  );
}
